import Link from 'next/link';
import styled, { css } from 'styled-components';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';

import { ACCOUNT_LIST } from 'profile/enums/constants';
import {
  AccountDropdownMenuButton,
  AccountDropdownMenuList,
  AccountDropdownMenuListItem,
  AccountDropdownMenuWrapper,
} from 'layout/header/styles/AccountDropdownMenu';
import { bugsnag } from 'shared/utils/services/bugsnag';
import { FeatureFlags } from 'featureFlags/constants';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { showOnMobileAndTabletOnly } from 'styles/utils/responsiveness';
import { UnorderedList } from 'shared/components';
import { useAccount } from 'account/hooks/useAccount';
import { useFeatureFlag } from 'shared/hooks';
import { useSitePreferencesModal } from 'shared/hooks/useSitePreferencesModal';
import { useSizePreferencesModal } from 'sizePreferences/hooks/useSizePreferencesModal';

const handleWantsClick = () => {
  sendTrackingEvent('WANTS_PAGE_TAP');
};

export const LoggedInDropdownMenu = () => {
  const { logOutAccount } = useAccount();
  const { pathname } = useRouter();
  const { openSitePreferencesModal } = useSitePreferencesModal();
  const { openSizePreferencesModal } = useSizePreferencesModal();
  const { t } = useTranslation();
  const selectedTab = pathname?.split('/')?.[2];
  const isProfileEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_PROFILE);
  const isOffersEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_WEB_OFFERS);
  const isOrdersEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_PROFILE_ORDERS);

  const handleLogOutClick = async () => {
    sendTrackingEvent('LOG_OUT_TAP');
    try {
      await logOutAccount();
    } catch (error) {
      bugsnag(error, { context: 'LoggedInDropdownMenu' });
    }
  };

  const handleSizePreferencesClick = () => {
    openSizePreferencesModal();
    sendTrackingEvent('SIZE_PREF_TAP');
  };

  const handleAnalyticsClick = (section) => {
    sendTrackingEvent('ACCOUNT_HEADER_NAVIGATION_TAP', { section });
  };

  if (isProfileEnabled) {
    return (
      <LoggedInDropdownMenu.Wrapper>
        <LoggedInDropdownMenu.List>
          <LoggedInDropdownMenu.Block>
            <LoggedInDropdownMenu.ListItem>
              <Link href="/wants" passHref>
                <LoggedInDropdownMenu.Link
                  data-qa="open_wants_page"
                  $isSelected={pathname === '/wants'}
                  onClick={() => handleAnalyticsClick('wants')}
                >
                  {t('profile.wants', 'Wants')}
                </LoggedInDropdownMenu.Link>
              </Link>
            </LoggedInDropdownMenu.ListItem>
            {isOrdersEnabled && (
              <LoggedInDropdownMenu.ListItem>
                <Link href="/account/orders" passHref>
                  <LoggedInDropdownMenu.Link
                    data-qa="account_nav_orders"
                    $isSelected={selectedTab === ACCOUNT_LIST.ORDERS}
                    onClick={() => handleAnalyticsClick('orders')}
                  >
                    {t('profile.orders', 'Orders')}
                  </LoggedInDropdownMenu.Link>
                </Link>
              </LoggedInDropdownMenu.ListItem>
            )}
            {isOffersEnabled && (
              <LoggedInDropdownMenu.ListItem>
                <Link href="/account/offers" passHref>
                  <LoggedInDropdownMenu.Link
                    data-qa="account_nav_offers"
                    $isSelected={selectedTab === ACCOUNT_LIST.OFFERS}
                    onClick={() => handleAnalyticsClick('offers')}
                  >
                    {t('profile.offers', 'Offers')}
                  </LoggedInDropdownMenu.Link>
                </Link>
              </LoggedInDropdownMenu.ListItem>
            )}
            <LoggedInDropdownMenu.ListItem>
              <Link href="/account/profile" passHref>
                <LoggedInDropdownMenu.Link
                  data-qa="account_nav_profile"
                  $isSelected={selectedTab === ACCOUNT_LIST.PROFILE}
                  onClick={() => handleAnalyticsClick('profile')}
                >
                  {t('profile.profile', 'Profile')}
                </LoggedInDropdownMenu.Link>
              </Link>
            </LoggedInDropdownMenu.ListItem>
            <LoggedInDropdownMenu.ListItem>
              <Link href="/account/preferences" passHref>
                <LoggedInDropdownMenu.Link
                  data-qa="account_nav_preferences"
                  $isSelected={selectedTab === ACCOUNT_LIST.PREFERENCES}
                  onClick={() => handleAnalyticsClick('preferences')}
                >
                  {t('profile.preferences', 'Preferences')}
                </LoggedInDropdownMenu.Link>
              </Link>
            </LoggedInDropdownMenu.ListItem>
            <LoggedInDropdownMenu.ListItem $hideOnDesktop>
              <Link href="/account/addresses" passHref>
                <LoggedInDropdownMenu.Link
                  data-qa="account_nav_addresses"
                  $isSelected={selectedTab === ACCOUNT_LIST.ADDRESSES}
                  onClick={() => handleAnalyticsClick('shipping_addresses')}
                >
                  {t('profile.shippingAddresses', 'Shipping Addresses')}
                </LoggedInDropdownMenu.Link>
              </Link>
            </LoggedInDropdownMenu.ListItem>
            <LoggedInDropdownMenu.ListItem $hideOnDesktop>
              <Link href="/account/payments" passHref>
                <LoggedInDropdownMenu.Link
                  data-qa="account_nav_payments"
                  $isSelected={selectedTab === ACCOUNT_LIST.PAYMENTS}
                  onClick={() => handleAnalyticsClick('payment_methods')}
                >
                  {t('profile.paymentMethods', 'Payment Methods')}
                </LoggedInDropdownMenu.Link>
              </Link>
            </LoggedInDropdownMenu.ListItem>
            <LoggedInDropdownMenu.ListItem>
              <Link href="https://support.goat.com/" passHref>
                <LoggedInDropdownMenu.Link
                  data-qa="account_nav_support"
                  $isSelected={selectedTab === ACCOUNT_LIST.SUPPORT}
                  onClick={() => handleAnalyticsClick('support')}
                >
                  {t('profile.support', 'Support')}
                </LoggedInDropdownMenu.Link>
              </Link>
            </LoggedInDropdownMenu.ListItem>
          </LoggedInDropdownMenu.Block>
          <LoggedInDropdownMenu.ListItem>
            <LoggedInDropdownMenu.Button
              data-qa="log_out_menu_button"
              onClick={handleLogOutClick}
              type="button"
            >
              Log&nbsp;Out
            </LoggedInDropdownMenu.Button>
          </LoggedInDropdownMenu.ListItem>
        </LoggedInDropdownMenu.List>
      </LoggedInDropdownMenu.Wrapper>
    );
  }

  return (
    <LoggedInDropdownMenu.Wrapper>
      <LoggedInDropdownMenu.List>
        <LoggedInDropdownMenu.ListItem>
          <Link href="/wants" passHref>
            <LoggedInDropdownMenu.Link data-qa="open_wants_page" onClick={handleWantsClick}>
              {t('layout.header.wants', 'Wants')}
            </LoggedInDropdownMenu.Link>
          </Link>
        </LoggedInDropdownMenu.ListItem>
        <LoggedInDropdownMenu.ListItem>
          <LoggedInDropdownMenu.Button
            data-qa="open_size_preferences"
            onClick={handleSizePreferencesClick}
            type="button"
          >
            Size&nbsp;Preferences
          </LoggedInDropdownMenu.Button>
        </LoggedInDropdownMenu.ListItem>
        <LoggedInDropdownMenu.ListItem>
          <LoggedInDropdownMenu.Button
            data-qa="open_location_preferences"
            onClick={openSitePreferencesModal}
            type="button"
          >
            {t('layout.header.locationPreferences', 'Location Preferences')}
          </LoggedInDropdownMenu.Button>
        </LoggedInDropdownMenu.ListItem>
        <LoggedInDropdownMenu.ListItem>
          <LoggedInDropdownMenu.Button
            data-qa="log_out_menu_button"
            onClick={handleLogOutClick}
            type="button"
          >
            Log&nbsp;Out
          </LoggedInDropdownMenu.Button>
        </LoggedInDropdownMenu.ListItem>
      </LoggedInDropdownMenu.List>
    </LoggedInDropdownMenu.Wrapper>
  );
};

LoggedInDropdownMenu.Wrapper = styled.div`
  ${AccountDropdownMenuWrapper};
`;

LoggedInDropdownMenu.Link = styled.a<{ $isSelected?: boolean }>`
  text-decoration: ${({ $isSelected }) => $isSelected && 'underline'};
`;

LoggedInDropdownMenu.Block = styled.div`
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    align-items: flex-end;
  }
`;

LoggedInDropdownMenu.List = styled(UnorderedList)`
  ${AccountDropdownMenuList};
`;

LoggedInDropdownMenu.ListItem = styled.li<{ $hideOnDesktop?: boolean }>`
  ${AccountDropdownMenuListItem};
  padding-bottom: 16px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    padding-bottom: 8px;
  }

  ${({ $hideOnDesktop }) =>
    $hideOnDesktop &&
    css`
      ${showOnMobileAndTabletOnly``};
    `}
`;

LoggedInDropdownMenu.Button = styled.button`
  ${AccountDropdownMenuButton};
`;
